import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CalendarPlus, Trash2, Save, Plus, X, EyeOff, Eye,
  Wrench, Coffee, Calendar, Star, PartyPopper, Bike,
} from "lucide-react";
import { API, inputCls, Loader, ErrorBox, EmptyState, Field } from "./shared";

const ICON_CHOICES = [
  { value: "wrench", label: "Wrench", Icon: Wrench },
  { value: "coffee", label: "Coffee", Icon: Coffee },
  { value: "calendar", label: "Calendar", Icon: Calendar },
  { value: "star", label: "Star", Icon: Star },
  { value: "party", label: "Party", Icon: PartyPopper },
  { value: "bike", label: "Bike", Icon: Bike },
];

const BADGE_COLORS = [
  { value: "red", label: "Red" },
  { value: "dark", label: "Dark" },
  { value: "green", label: "Green" },
];

function newBlankWeekly() {
  return {
    id: `weekly-${Date.now()}`,
    title: "",
    schedule: "",
    description: "",
    bullets: [],
    icon: "calendar",
    badge_color: "red",
    rsvp_instagram: "",
    rsvp_sms: "",
    hidden: false,
  };
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function newBlankUpcoming() {
  return {
    title: "",
    date: todayIso(),
    time: "",
    location: "",
    description: "",
    link: "",
    hidden: false,
  };
}

// ---------- Weekly events editor ----------
function WeeklySection() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setItems(Array.isArray(data.weekly_events) ? data.weekly_events : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const updateBullet = (idx, bIdx, val) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const bullets = [...(it.bullets || [])];
        bullets[bIdx] = val;
        return { ...it, bullets };
      })
    );
  };

  const addBullet = (idx) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, bullets: [...(it.bullets || []), ""] } : it))
    );
  };

  const removeBullet = (idx, bIdx) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        return { ...it, bullets: (it.bullets || []).filter((_, j) => j !== bIdx) };
      })
    );
  };

  const addEvent = () => setItems((prev) => [...prev, newBlankWeekly()]);
  const removeEvent = (idx) => {
    if (!window.confirm("Remove this weekly event?")) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const cleaned = items.map((it) => ({
        ...it,
        bullets: (it.bullets || []).map((b) => b.trim()).filter(Boolean),
      }));
      await axios.put(
        `${API}/settings`,
        { weekly_events: cleaned },
        { withCredentials: true }
      );
      setItems(cleaned);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (items === null) return <Loader />;

  return (
    <section
      data-testid="admin-weekly-events"
      className="rounded-3xl border-2 border-leaf-100 bg-white p-6"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-leaf-900">Weekly events</h2>
          <p className="text-sm text-leaf-900/60">
            Standing gatherings shown on the /events page (e.g. Mechanical Mondays).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {savedFlash && (
            <span className="rounded-full bg-leaf-600/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-leaf-700">
              Saved
            </span>
          )}
          <button
            data-testid="admin-weekly-add"
            onClick={addEvent}
            className="inline-flex items-center gap-2 rounded-full border-2 border-leaf-300 bg-white px-4 py-2 text-sm font-bold text-leaf-800 hover:bg-leaf-50"
          >
            <Plus className="h-4 w-4" /> Add weekly event
          </button>
          <button
            data-testid="admin-weekly-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-leaf-600 px-4 py-2 text-sm font-bold text-white hover:bg-leaf-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {err && <ErrorBox msg={err} />}

      {items.length === 0 && (
        <EmptyState icon={CalendarPlus} label="No weekly events yet — click Add" />
      )}

      <div className="space-y-4">
        {items.map((it, idx) => (
          <div
            key={it.id || idx}
            data-testid={`admin-weekly-item-${idx}`}
            className={`rounded-2xl border-2 p-5 ${
              it.hidden ? "border-stone-200 bg-stone-50/60" : "border-leaf-100 bg-leaf-50/30"
            }`}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <span className="font-display text-lg font-bold text-leaf-900">
                {it.title || "Untitled weekly event"}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  data-testid={`admin-weekly-toggle-hidden-${idx}`}
                  onClick={() => update(idx, { hidden: !it.hidden })}
                  title={it.hidden ? "Show on site" : "Hide from site"}
                  className="rounded-full p-2 text-leaf-700 hover:bg-leaf-100"
                >
                  {it.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button
                  data-testid={`admin-weekly-remove-${idx}`}
                  onClick={() => removeEvent(idx)}
                  className="rounded-full p-2 text-clay-500 hover:bg-clay-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Title">
                <input
                  data-testid={`admin-weekly-title-${idx}`}
                  className={inputCls}
                  value={it.title || ""}
                  onChange={(e) => update(idx, { title: e.target.value })}
                />
              </Field>
              <Field label="Schedule badge">
                <input
                  data-testid={`admin-weekly-schedule-${idx}`}
                  className={inputCls}
                  placeholder="Mondays · 3–6 PM"
                  value={it.schedule || ""}
                  onChange={(e) => update(idx, { schedule: e.target.value })}
                />
              </Field>
              <Field label="Description" full>
                <textarea
                  data-testid={`admin-weekly-desc-${idx}`}
                  className={`${inputCls} min-h-[80px]`}
                  value={it.description || ""}
                  onChange={(e) => update(idx, { description: e.target.value })}
                />
              </Field>
              <Field label="Icon">
                <select
                  data-testid={`admin-weekly-icon-${idx}`}
                  className={inputCls}
                  value={it.icon || "calendar"}
                  onChange={(e) => update(idx, { icon: e.target.value })}
                >
                  {ICON_CHOICES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Badge color">
                <select
                  data-testid={`admin-weekly-badge-${idx}`}
                  className={inputCls}
                  value={it.badge_color || "red"}
                  onChange={(e) => update(idx, { badge_color: e.target.value })}
                >
                  {BADGE_COLORS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="RSVP Instagram URL (optional)">
                <input
                  data-testid={`admin-weekly-ig-${idx}`}
                  className={inputCls}
                  placeholder="https://instagram.com/laststopstl"
                  value={it.rsvp_instagram || ""}
                  onChange={(e) => update(idx, { rsvp_instagram: e.target.value })}
                />
              </Field>
              <Field label="RSVP SMS number (optional, E.164)">
                <input
                  data-testid={`admin-weekly-sms-${idx}`}
                  className={inputCls}
                  placeholder="+13143918326"
                  value={it.rsvp_sms || ""}
                  onChange={(e) => update(idx, { rsvp_sms: e.target.value })}
                />
              </Field>
            </div>

            <div className="mt-4">
              <span className="mb-1.5 block text-sm font-semibold text-leaf-900">Bullet points</span>
              <div className="space-y-2">
                {(it.bullets || []).map((b, bIdx) => (
                  <div key={bIdx} className="flex items-center gap-2">
                    <input
                      data-testid={`admin-weekly-bullet-${idx}-${bIdx}`}
                      className={inputCls}
                      value={b}
                      onChange={(e) => updateBullet(idx, bIdx, e.target.value)}
                    />
                    <button
                      onClick={() => removeBullet(idx, bIdx)}
                      className="rounded-full p-2 text-clay-500 hover:bg-clay-500/10"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  data-testid={`admin-weekly-add-bullet-${idx}`}
                  onClick={() => addBullet(idx)}
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-leaf-300 px-3 py-1 text-xs font-bold text-leaf-700 hover:bg-leaf-50"
                >
                  <Plus className="h-3.5 w-3.5" /> Add bullet
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- Upcoming (one-off) events ----------
function UpcomingSection() {
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState(newBlankUpcoming());
  const [err, setErr] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/events?include_hidden=true&include_past=true`,
        { withCredentials: true }
      );
      setItems(data || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load events");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.title.trim() || !draft.date) {
      setActionErr("Title and date are required");
      return;
    }
    setCreating(true);
    setActionErr("");
    try {
      const { data } = await axios.post(`${API}/events`, draft, { withCredentials: true });
      setItems((prev) => [...(prev || []), data].sort((a, b) => a.date.localeCompare(b.date)));
      setDraft(newBlankUpcoming());
    } catch (e) {
      setActionErr(e?.response?.data?.detail || "Failed to create event");
    } finally {
      setCreating(false);
    }
  };

  const patch = async (id, changes) => {
    try {
      const { data } = await axios.patch(`${API}/events/${id}`, changes, { withCredentials: true });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
    } catch (e) {
      setActionErr(e?.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this upcoming event?")) return;
    try {
      await axios.delete(`${API}/events/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) {
      setActionErr(e?.response?.data?.detail || "Failed to delete");
    }
  };

  if (items === null) return <Loader />;

  const todayStr = todayIso();

  return (
    <section
      data-testid="admin-upcoming-events"
      className="rounded-3xl border-2 border-leaf-100 bg-white p-6"
    >
      <div className="mb-4">
        <h2 className="font-display text-2xl font-bold text-leaf-900">Upcoming events</h2>
        <p className="text-sm text-leaf-900/60">
          One-off dates: pop-ups, races, group rides, closures. Auto-hidden from the site after their date passes.
        </p>
      </div>

      {err && <ErrorBox msg={err} />}
      {actionErr && <ErrorBox msg={actionErr} />}

      {/* Create form */}
      <div
        data-testid="admin-upcoming-create"
        className="mb-6 rounded-2xl border-2 border-dashed border-leaf-200 bg-leaf-50/40 p-4"
      >
        <p className="mb-3 text-sm font-bold uppercase tracking-wider text-leaf-700">Add a new one</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Title *">
            <input
              data-testid="admin-upcoming-title"
              className={inputCls}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </Field>
          <Field label="Date *">
            <input
              data-testid="admin-upcoming-date"
              type="date"
              className={inputCls}
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
          </Field>
          <Field label="Time (optional)">
            <input
              data-testid="admin-upcoming-time"
              className={inputCls}
              placeholder="10:00 AM or 10 AM – 2 PM"
              value={draft.time}
              onChange={(e) => setDraft({ ...draft, time: e.target.value })}
            />
          </Field>
          <Field label="Location (optional)">
            <input
              data-testid="admin-upcoming-location"
              className={inputCls}
              placeholder="Noteworthy Café, Tower Grove Park…"
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
            />
          </Field>
          <Field label="Description (optional)" full>
            <textarea
              data-testid="admin-upcoming-desc"
              className={`${inputCls} min-h-[70px]`}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </Field>
          <Field label="RSVP or info link (optional)" full>
            <input
              data-testid="admin-upcoming-link"
              className={inputCls}
              placeholder="https://…"
              value={draft.link}
              onChange={(e) => setDraft({ ...draft, link: e.target.value })}
            />
          </Field>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            data-testid="admin-upcoming-create-btn"
            onClick={create}
            disabled={creating}
            className="inline-flex items-center gap-2 rounded-full bg-leaf-600 px-4 py-2 text-sm font-bold text-white hover:bg-leaf-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> {creating ? "Adding…" : "Add event"}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={CalendarPlus} label="No upcoming events yet" />
      ) : (
        <div className="space-y-3">
          {items.map((ev) => {
            const isPast = ev.date < todayStr;
            return (
              <div
                key={ev.id}
                data-testid={`admin-upcoming-item-${ev.id}`}
                className={`rounded-2xl border-2 p-4 ${
                  ev.hidden || isPast
                    ? "border-stone-200 bg-stone-50/60"
                    : "border-leaf-100 bg-white"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-lg font-bold text-leaf-900">
                        {ev.title}
                      </h3>
                      <span className="rounded-full bg-leaf-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
                        {ev.date}
                      </span>
                      {ev.time && (
                        <span className="rounded-full bg-leaf-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-leaf-700">
                          {ev.time}
                        </span>
                      )}
                      {isPast && (
                        <span className="rounded-full bg-stone-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-stone-600">
                          Past
                        </span>
                      )}
                      {ev.hidden && (
                        <span className="rounded-full bg-clay-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-clay-600">
                          Hidden
                        </span>
                      )}
                    </div>
                    {ev.location && (
                      <p className="mt-1 text-sm text-leaf-900/70">📍 {ev.location}</p>
                    )}
                    {ev.description && (
                      <p className="mt-1 text-sm text-leaf-900/85 whitespace-pre-wrap">
                        {ev.description}
                      </p>
                    )}
                    {ev.link && (
                      <a
                        href={ev.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-block text-sm text-leaf-600 hover:underline"
                      >
                        {ev.link}
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid={`admin-upcoming-toggle-${ev.id}`}
                      onClick={() => patch(ev.id, { hidden: !ev.hidden })}
                      title={ev.hidden ? "Show on site" : "Hide from site"}
                      className="rounded-full p-2 text-leaf-700 hover:bg-leaf-100"
                    >
                      {ev.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button
                      data-testid={`admin-upcoming-delete-${ev.id}`}
                      onClick={() => remove(ev.id)}
                      className="rounded-full p-2 text-clay-500 hover:bg-clay-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function EventsTab() {
  return (
    <div data-testid="admin-events" className="space-y-6">
      <WeeklySection />
      <UpcomingSection />
    </div>
  );
}
